import React, { useEffect, useRef, useState } from 'react';
import { Button, Image, Input } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import api, { getStaticImageUrl } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

interface Props {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onUploadingChange: (uploading: boolean) => void;
  appearance?: 'workspace' | 'default';
  label?: string;
  imageAlt?: string;
  fileLabel?: string;
  previewClassName?: string;
  compact?: boolean;
}

const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

/** Фото вопроса или варианта; снятие ссылки не удаляет файл из хранилища. */
const QuestionImageField: React.FC<Props> = ({ id, value, onChange, onUploadingChange, appearance = 'default',
  label = 'Изображение к вопросу (необязательно)', imageAlt = 'Изображение вопроса',
  fileLabel = 'Файл изображения вопроса', previewClassName, compact = false }) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const request = useRef<AbortController | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const [expanded, setExpanded] = useState(!compact);
  const busyCallback = useRef(onUploadingChange);
  const changeCallback = useRef(onChange);
  busyCallback.current = onUploadingChange;
  changeCallback.current = onChange;
  const workspace = appearance === 'workspace';
  const imageUrl = value.trim();

  useEffect(() => {
    setPreviewFailed(false);
  }, [value]);

  useEffect(() => () => {
    request.current?.abort();
    busyCallback.current(false);
  }, []);

  const upload = async (file: File) => {
    setError('');
    if (file.size > MAX_IMAGE_BYTES) {
      setError('Изображение превышает допустимый размер 15 МБ');
      return;
    }
    const controller = new AbortController();
    request.current = controller;
    setUploading(true);
    busyCallback.current(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await api.post('/images/upload', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (typeof data?.url !== 'string' || !data.url.trim()) {
        throw new Error('Сервер не вернул ссылку на изображение');
      }
      changeCallback.current(data.url.trim());
    } catch (uploadError) {
      if (!controller.signal.aborted) {
        setError(getApiErrorMessage(uploadError, 'Не удалось загрузить изображение. Попробуйте ещё раз.'));
      }
    } finally {
      if (!controller.signal.aborted) {
        request.current = null;
        setUploading(false);
        busyCallback.current(false);
      }
    }
  };

  const changeUrl = (next: string) => {
    setError('');
    onChange(next);
  };
  const inputProps = {
    id,
    value,
    disabled: uploading,
    placeholder: 'Вставьте ссылку на изображение',
    'aria-describedby': `${id}-help`,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => changeUrl(event.target.value),
  };

  if (compact && !expanded && !imageUrl) {
    const buttonProps = {
      'aria-label': `Добавить фото или ссылку: ${imageAlt}`,
      'aria-expanded': false,
      onClick: () => setExpanded(true),
    };
    return workspace ? (
      <button type="button" className="tw-btn tw-btn-quiet tw-btn-small" {...buttonProps}>Добавить фото или ссылку</button>
    ) : <Button type="link" size="small" {...buttonProps}>Добавить фото или ссылку</Button>;
  }

  return (
    <div className={workspace ? 'tw-stack-sm' : undefined} style={workspace ? undefined : { display: 'grid', gap: 8 }}>
      <div>
        <label htmlFor={id} className={workspace ? 'tw-label' : undefined}>{label}</label>
        {workspace ? <input {...inputProps} className="tw-field" type="text" /> : <Input {...inputProps} />}
      </div>
      <div className={workspace ? 'tw-row tw-btn-row' : undefined}>
        <input
          ref={fileInput}
          type="file"
          aria-label={fileLabel}
          accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif,.mpo"
          hidden
          disabled={uploading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void upload(file);
          }}
        />
        {workspace ? (
          <button type="button" className="tw-btn" disabled={uploading} onClick={() => fileInput.current?.click()}>
            <UploadOutlined /> {uploading ? 'Загрузка…' : 'Загрузить фото'}
          </button>
        ) : (
          <Button icon={<UploadOutlined />} loading={uploading} onClick={() => fileInput.current?.click()}>Загрузить фото</Button>
        )}
      </div>
      <p id={`${id}-help`} className={workspace ? 'tw-help' : undefined} style={workspace ? undefined : { margin: 0, color: '#667085' }}>
        По ссылке или файлом: JPEG, PNG, WEBP, GIF до 15 МБ.
      </p>
      {error && <div role="alert">{error}</div>}
      {imageUrl && (
        <div>
          <Image
            src={getStaticImageUrl(imageUrl)}
            alt={imageAlt}
            className={previewClassName}
            width={compact ? 160 : undefined}
            style={{ maxWidth: '100%', maxHeight: compact ? 160 : 180, objectFit: 'contain' }}
            onError={() => setPreviewFailed(true)}
          />
          {previewFailed && <p role="status">Не удалось открыть предпросмотр. Проверьте ссылку на изображение.</p>}
          <div style={{ marginTop: 8 }}>
            {workspace ? (
              <button type="button" className="tw-btn" disabled={uploading} onClick={() => changeUrl('')}>Убрать фото</button>
            ) : <Button disabled={uploading} onClick={() => changeUrl('')}>Убрать фото</Button>}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionImageField;
